import { useParams, Link } from 'react-router-dom';
import { Box, Container, Typography, Stack, Chip, Avatar, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useLanguage } from '../contexts/LanguageContext';
import { blogPosts } from '../data/blogMockData';
import BlogPostCard from '../components/BlogPostCard';
import SEOHead from '../components/SEOHead';
import { formatDate, formatDateEN, formatReadTime, formatCategory } from '../utils/formatters';

const HeroImage = styled(Box)({
  width: '100%',
  height: '500px',
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '32px',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
});

const ContentSection = styled(Box)(({ theme }) => ({
  padding: '160px 0 80px',
  background: theme.palette.mode === 'dark'
    ? '#0F172A'
    : '#FFFFFF',
}));

const ArticleContent = styled(Box)(({ theme }) => ({
  '& h2': {
    fontSize: '2rem',
    fontWeight: 700,
    marginTop: '2.5rem',
    marginBottom: '1rem',
    color: theme.palette.text.primary,
  },
  '& h3': {
    fontSize: '1.5rem',
    fontWeight: 600,
    marginTop: '2rem',
    marginBottom: '0.75rem',
    color: theme.palette.text.primary,
  },
  '& p': {
    fontSize: '1.1rem',
    lineHeight: 1.9,
    marginBottom: '1.5rem',
    color: theme.palette.text.secondary,
  },
  '& ul, & ol': {
    fontSize: '1.1rem',
    lineHeight: 1.9,
    marginBottom: '1.5rem',
    paddingLeft: '2rem',
    color: theme.palette.text.secondary,
  },
  '& li': {
    marginBottom: '0.5rem',
  },
  '& strong': {
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
}));

const BackButton = styled(Button)(({ theme }) => ({
  marginBottom: '2rem',
  color: theme.palette.text.primary,
  fontWeight: 600,
  '&:hover': {
    background: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.03)',
  },
}));

export default function BlogPostDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { language } = useLanguage();

  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return (
      <Container maxWidth="lg" sx={{ py: 20, textAlign: 'center' }}>
        <Typography variant="h4">
          {language === 'tr' ? 'Yazı bulunamadı' : 'Post not found'}
        </Typography>
        <Button component={Link} to="/blog" sx={{ mt: 3 }}>
          {language === 'tr' ? 'Bloga Dön' : 'Back to Blog'}
        </Button>
      </Container>
    );
  }

  const title = language === 'tr' ? post.titleTR : post.titleEN;
  const content = language === 'tr' ? post.contentTR : post.contentEN;
  const formattedDate = language === 'tr' ? formatDate(post.publishDate) : formatDateEN(post.publishDate);
  const readTime = formatReadTime(post.readTime, language);
  const category = formatCategory(post.category, language);

  const relatedPosts = blogPosts
    .filter(p => p.id !== post.id && p.category === post.category)
    .slice(0, 3);

  return (
    <>
      <SEOHead 
        title={`${title} - Teknolobi Blog`}
        description={language === 'tr' ? post.excerptTR : post.excerptEN}
      />
      
      <ContentSection component="main" role="main">
        <Container maxWidth="lg">
          <BackButton
            component={Link}
            to="/blog"
            startIcon={<ArrowBackIcon />}
          >
            {language === 'tr' ? 'Tüm Yazılar' : 'All Posts'}
          </BackButton>

          <Stack spacing={4}>
            {/* Header */}
            <Box>
              <Chip 
                label={category} 
                sx={{ 
                  mb: 3,
                  background: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(16, 185, 129, 0.2)'
                    : 'rgba(37, 99, 235, 0.15)',
                  color: (theme) => theme.palette.mode === 'dark' 
                    ? theme.palette.secondary.light 
                    : theme.palette.primary.main,
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  padding: '8px 4px',
                }}
              />
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2rem', md: '3rem' },
                  mb: 3,
                  lineHeight: 1.2,
                }}
              >
                {title}
              </Typography>

              <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar src={post.author.avatar} alt={post.author.name} sx={{ width: 48, height: 48 }} />
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      {post.author.name}
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {formattedDate}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {readTime}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                </Stack>
              </Stack>
            </Box>

            {/* Featured Image */}
            <HeroImage>
              <img src={post.featuredImage} alt={title} />
            </HeroImage>

            {/* Content */}
            <ArticleContent>
              {content.split('\n').map((paragraph, index) => {
                if (paragraph.startsWith('## ')) {
                  return <Typography key={index} component="h2">{paragraph.replace('## ', '')}</Typography>;
                } else if (paragraph.startsWith('### ')) {
                  return <Typography key={index} component="h3">{paragraph.replace('### ', '')}</Typography>;
                } else if (paragraph.trim()) {
                  return <Typography key={index} component="p">{paragraph}</Typography>;
                }
                return null;
              })}
            </ArticleContent>

            {/* Tags */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {post.tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  size="small"
                  sx={{
                    background: (theme) => theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(0, 0, 0, 0.05)',
                  }}
                />
              ))}
            </Stack>

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
              <Box sx={{ mt: 8 }}>
                <Typography
                  variant="h3"
                  component="h2"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '1.75rem', md: '2.5rem' },
                    mb: 4,
                    background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {language === 'tr' ? 'İlgili Yazılar' : 'Related Posts'}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: 'repeat(3, 1fr)',
                    },
                    gap: 4,
                  }}
                >
                  {relatedPosts.map((relatedPost) => (
                    <BlogPostCard key={relatedPost.id} post={relatedPost} />
                  ))}
                </Box>
              </Box>
            )}
          </Stack>
        </Container>
      </ContentSection>
    </>
  );
}